import { LitElement, html } from 'lit';

/**
 * Data Services Special Terms Content
 * Renders the full legal text for Data Services special terms.
 * Content matches the official PDF word-for-word.
 */
class DataSpecialTerms extends LitElement {
    createRenderRoot() {
        return this;
    }

    render() {
        return html`
            <div class="special-terms">
                <h4 class="text-base font-bold mb-4">DATA SERVICES - SPECIAL TERMS</h4>

                <p class="mb-3">
                    The <strong>Data Services</strong> are described in the Statement of Work
                    between Client and REACH.
                </p>

                <h5>1. ABOUT THESE SPECIAL TERMS</h5>

                <p>
                    1.1 These Special Terms for Data Services sets out the Special Terms that apply
                    to Data Services acquired by the Client through the Booking Confirmation and/or
                    Statement of Work.
                </p>

                <h5>2. DEFINITIONS</h5>

                <p>
                    2.1 For the purposes of these Special Terms, the following terms have the
                    following meanings:
                </p>

                <p class="ml-6">
                    (a) <strong>Data Services</strong> means the data services set out in a Booking
                    Confirmation or Statement of Work for Data Services.
                </p>

                <p class="ml-6">
                    (b) <strong>Deliverable</strong> means any deliverable specified in a Booking
                    Confirmation or Statement of Work, as applicable.
                </p>

                <p>
                    2.2 Terms not defined in these Special Terms have the same meaning as the Terms
                    and Conditions.
                </p>

                <h5>3. DATA SERVICES</h5>

                <p>
                    3.1 The Client will use reasonable endeavours to ensure that the Client Data is
                    accurate, up to date, complete and not misleading, and will update REACH if any
                    of the information contained in the Client Date becomes inaccurate or requires
                    updating. REACH will not be required to review or verify Client Data and will
                    not be responsible for any inaccuracy or omission in its performance of any Data
                    Services to the extent such inaccuracies or omissions were present in or
                    otherwise attributable to Client Data provided by the Client.
                </p>

                <p>
                    3.2 Client acknowledges that any Deliverables provided by REACH that rely on the
                    Client Data are only as accurate and useful as the Client Data provided. REACH
                    does not guarantee any results related to the Deliverables or other outcomes of
                    the Data Services.
                </p>

                <p>
                    3.3 Milestones in the Booking Confirmation and/or Statement of Work are
                    indicative only, and are based on timely performance of the Client's
                    responsibilities (including provision of information, instructions and approvals
                    reasonably requested by REACH), and REACH being in possession of accurate and
                    complete Client Data. If the Client fails to comply with its obligations under
                    this clause 3.3, REACH shall not be liable for failing to meet any Milestones.
                </p>

                <p>
                    3.4 <strong>Acceptance testing:</strong> If the Booking Confirmation and/or
                    Statement of Work provides that a Deliverable is subject to acceptance testing,
                    the Client will, following receipt of the Deliverable, test the relevant
                    Deliverable in accordance with an agreed acceptance test plan.
                </p>

                <p class="ml-6">
                    (a) If the testing demonstrates that the Deliverable meets the acceptance
                    criteria, or if the Client does not notify REACH within five Business Days of
                    receipt of the Deliverable of non-acceptance, then the Deliverable will be
                    deemed accepted.
                </p>

                <p class="ml-6">
                    (b) If the testing does not demonstrate that the Deliverable meets the
                    acceptance criteria, the Client will notify REACH specifying the particular
                    failures, and REACH shall remedy those failures within 10 Business Days of
                    notification. If Client does not provide notice under this clause within 10
                    Business Days of receipt of the Deliverable, it will be deemed to have accepted
                    that Deliverable.
                </p>

                <p>
                    3.5 <strong>Third party data and software:</strong> If third party data or
                    software is provided as part of the Services (for example, to supplement or
                    enhance the Client Data), the Client acknowledges that it may be required to
                    comply with applicable third party data source or software terms, as notified by
                    REACH from time to time.
                </p>

                <p>
                    3.6 <strong>Reach data:</strong> The Client acknowledges that all Intellectual
                    Property Rights in data, information and other material made available to the
                    Client by REACH (<strong>REACH Data</strong>) remains owned by REACH (or its
                    licensors). Notwithstanding clause 6.3 of the Terms and Conditions and subject
                    to any terms specified in the Booking Confirmation and/or Statement of Work,
                    REACH grants to the Client a licence for a term of to use the REACH Data solely
                    be used in connection with the relevant Deliverable.
                </p>

                <p>
                    3.7 The Client acknowledges that third party data or REACH Data may include
                    Personal Information and therefore be subject to the Privacy Act 2020. Where the
                    Client comes into possession of such Personal Information, the Client will be
                    responsible for ensuring its own compliance with the Information Privacy
                    Principles (<strong>IPPs</strong>) set out in the Privacy Act in respect of such
                    Personal Information. In particular, the Client acknowledges that any use by
                    REACH of any REACH Data or third party data in the course of providing the Data
                    Services and the Deliverables is collected and processed on behalf of the
                    Client, and the Client is therefore responsible for satisfying itself that IPP 3
                    and (to the extent applicable) IPP 3A of the Privacy Act 2020 are complied with.
                </p>

                <p>
                    3.8 Unless the Client otherwise instructs REACH in writing, REACH will securely
                    dispose of or return the Client Data to the extent it is held by REACH or a
                    sub-contractor within 5 Business Days once the Client advises it is no longer
                    required for the purposes of the provision of the Services and upon the Client
                    providing notice following expiry or termination. The Client acknowledges that
                    REACH will not be responsible for maintaining a copy of any Client Data, and
                    that the Client is responsible for maintaining all necessary back-ups of the
                    Client Data.
                </p>

                <h5>4. PERSONAL INFORMATION</h5>

                <p>
                    4.1 To the extent that the Client provides or makes available Personal
                    Information to REACH, or REACH otherwise has access to, stores, processes or
                    transmits Personal Information under or in connection with the Agreement, this
                    clause 4 shall apply.
                </p>

                <p>4.2 REACH must:</p>

                <p class="ml-6">
                    (a) use, access, store, process or transmit the Personal Information only to the
                    extent necessary to provide the Services;
                </p>

                <p class="ml-6">
                    (b) protect the Personal Information with such security safeguards that are
                    reasonable in the circumstances; and
                </p>

                <p class="ml-6">
                    (c) notify the Client if any Privacy Breach (as defined in the Privacy Act 2020)
                    occurs in respect of any Personal Information.
                </p>

                <p>
                    4.3 The Client must arrange all necessary consents and approvals reasonably
                    required for REACH to use and process the Personal Information in accordance
                    with this Agreement.
                </p>
            </div>
        `;
    }
}

customElements.define('data-special-terms', DataSpecialTerms);
